/**
 * Wave 14 Agent TRIAL - Layer 3.
 *
 * 5-step "first 5 minutes" tour. Lightweight overlay; no third-party
 * dep. Steps anchor to data-tour-id attributes on the target elements
 * but fall back to a centred card when the anchor is missing.
 *
 * Persists completion in localStorage so we never bother a converting
 * tenant twice.
 */
import { useEffect, useMemo, useState } from 'react'

const STORAGE_KEY = 'wave14_onboarding_tour_done'

interface Step {
  id: string
  title: string
  body: string
  anchor?: string
  ctaLabel?: string
  ctaHref?: string
}

const STEPS: Step[] = [
  {
    id: 'welcome',
    title: 'Welcome to ORKSTRA',
    body: 'You have 14 days of full access. Hit any milestone for a +7 day extension.',
    ctaLabel: "Let's go",
  },
  {
    id: 'upload-boq',
    title: '1) Upload a real BOQ',
    body: 'Import an Excel BOQ -- our Auto-Mapping engine starts learning instantly.',
    anchor: 'tour:boq-importer',
    ctaLabel: 'Open BOQ Importer',
    ctaHref: '/boq',
  },
  {
    id: 'invite-team',
    title: '2) Invite your team',
    body: 'Five teammates unlocks collaborative review. We extend your trial too.',
    anchor: 'tour:invite-team',
    ctaLabel: 'Open team settings',
    ctaHref: '/settings/team',
  },
  {
    id: 'project-setup',
    title: '3) Configure your first project',
    body: 'Pick the client, region, currency, and Hijri/Gregorian dates.',
    anchor: 'tour:project-setup',
    ctaLabel: 'Open projects',
    ctaHref: '/projects',
  },
  {
    id: 'talk-to-sales',
    title: '4) Talk to sales',
    body: 'Book a demo if you want a personalised walk-through. It also unlocks +7 days.',
    anchor: 'tour:demo',
    ctaLabel: 'Book a demo',
    ctaHref: '/pilot/apply',
  },
]

interface OnboardingTourProps {
  forceOpen?: boolean
  onClose?: () => void
}

export function OnboardingTour({ forceOpen, onClose }: OnboardingTourProps) {
  const [open, setOpen] = useState<boolean>(false)
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    if (forceOpen) {
      setOpen(true)
      return
    }
    try {
      const done = localStorage.getItem(STORAGE_KEY) === '1'
      setOpen(!done)
    } catch {
      setOpen(true)
    }
  }, [forceOpen])

  const step = useMemo(() => STEPS[stepIndex], [stepIndex])

  if (!open) return null

  const close = (markDone: boolean) => {
    if (markDone) {
      try {
        localStorage.setItem(STORAGE_KEY, '1')
      } catch {
        /* noop */
      }
    }
    setOpen(false)
    onClose?.()
  }

  const isLast = stepIndex === STEPS.length - 1

  return (
    <div
      data-testid="onboarding-tour"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">{step.title}</h3>
          <button
            type="button"
            onClick={() => close(true)}
            className="text-slate-500 hover:text-slate-800"
            aria-label="Close tour"
          >
            ×
          </button>
        </div>
        <p className="text-slate-700">{step.body}</p>
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-slate-500">
            Step {stepIndex + 1} of {STEPS.length}
          </div>
          <div className="flex items-center gap-2">
            {step.ctaHref && (
              <a
                href={step.ctaHref}
                className="text-sm px-3 py-1 rounded border border-blue-600 text-blue-700 hover:bg-blue-50"
              >
                {step.ctaLabel || 'Open'}
              </a>
            )}
            {!isLast ? (
              <button
                type="button"
                onClick={() => setStepIndex((i) => i + 1)}
                className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={() => close(true)}
                className="text-sm px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingTour
